<template>
  <section>
    <div class="columns">
      <b-field
        v-bind="fieldAttr"
        class="column">
        <b-autocomplete
          v-model="search"
          :data="contactsDataFilter"
          :placeholder="placeholder"
          :loading="isFetchingContact"
          @select="actionSelectContact"
          field="nomPrenom"
          icon="magnify"
          class="exception-input"
          open-on-focus>
          <template #header>
            <a v-if="ajoutContact && !!tiers && !!tiers.id" class="lien"
              @click="checkPermissions(constantes.TIERS.maj) ? ajouterContact() : showPopUp()"
              :class="{ 'text-access-no-permission' : !checkPermissions(constantes.TIERS.maj) }">
              {{$t('ajoutDevis.table.editionContacts.addContactToClient')}}
            </a>
          </template>
          <template #option>
            <span class="has-text-weight-bold">
              {{ option.nomPrenom }}
            </span>
            <br>
            <span class="is-italic is-size-7">
              {{ getTypesContacts(option.typesContactIds) }}
            </span>
          </template>
          <template #empty>
            {{$t(`general.autocomplete.${!isFetchingContact ? 'noRslt' : 'search'}`)}}
          </template>
        </b-autocomplete>
      </b-field>
      <a
        v-if="deletable"
        class="mgt-45px column is-narrow is-paddingless"
        @click="actionResetValue">
        <b-icon icon="delete-forever" type="is-danger"></b-icon>
      </a>
    </div>
    <b-modal
      v-model="popUpAjoutContact"
      class="modal"
      has-modal-card>
      <ajouter-contact
        v-if="tiers"
        :tiers="tiers"
        :idTiers="tiers.id"
        :contact="contactAdded"
        :contacts="tiers.contacts"
        @added="$emit('added', $event)"/>
    </b-modal>
  </section>
</template>

<script>
import { uniqBy } from 'lodash';
import { createNamespacedHelpers } from 'vuex';
import { getAllByTiers } from '@/api/contactTiers/index';
import { getAllWithTiers as getAllContactByTiers } from '@/api/contacts';
import contactGenerer from '@/services/tiers/genererContact';
import messageErreur from '@/services/general/messageErreur';
import { getContactFullNameWithInfo } from '@/services/contact/contactHelper';
import { TIERS } from '@/configuration/permissionsBodacConf';
import { PARAMETRES_KEYS, PARAMETRES_INTERNE_KEYS } from '@/store/modules/parametres-mutation-type';
import AjouterContact from '@/components/clients/clients/editionClient/ajouterContact';

const Societe = createNamespacedHelpers('societe');
const Parametres = createNamespacedHelpers('parametres');
const Permissions = createNamespacedHelpers('permissions');

export default {
  props: {
    value: {
      type: Object,
      required: false,
    },
    tiers: {
      type: Object,
      required: false,
    },
    funcFilter: {
      type: Function,
      required: false,
      default: () => () => true,
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    ajoutContact: {
      type: Boolean,
      required: false,
      default: true,
    },
    deletable: {
      type: Boolean,
      required: false,
      default: false,
    },
    withContactPortail: {
      type: Boolean,
      required: false,
      default: false,
    },
    withContactSuperieur: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    obligatoire: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      search: '',
      popUpAjoutContact: false,
      isFetchingContact: false,
      contactAdded: null,
      contactsData: [],
      constantes: {
        TIERS,
      },
    };
  },
  components: {
    AjouterContact,
  },
  watch: {
    tiers: {
      async handler(val) {
        this.contactsData = await this.getContactData();
        if (!val) this.actionResetValue();
      },
    },
  },
  computed: {
    ...Parametres.mapGetters({
      parametre: 'parametre',
      parametresInterne: 'parametresInterne',
    }),
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    contactsVenteSuperieur() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.contactsVenteSuperieur.key);
    },
    modulePortailClientActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.modulePortailClient.key);
    },
    typeContacts() {
      return this.parametre(PARAMETRES_KEYS.typeContact).map(t => ({
        libelle: t.libelle,
        typeContactType: t.typeContactType,
        typeContactId: t.id,
      }));
    },
    contactsDataFilter() {
      return this.contactsData
        .filter(c => c.nomPrenom.toLowerCase().includes(this.search.toLowerCase())
          && this.funcFilter(c));
    },
    contactSelected: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    fieldAttr() {
      return {
        message: this.contactSelected?.archive ? this.$t('detailContact.warning.archivedContactSelectedAbove') : '',
        type: this.contactSelected?.archive ? 'is-warning' : '',
        label: this.label,
      };
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
      loadParametresInterne: 'loadParametresInterne',
    }),
    ...Permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    async getContactData() {
      const { contacts, tiersParentId, id: tiersId } = this.tiers || {};
      const promises = [];
      if (this.withContactSuperieur && this.contactsVenteSuperieur && !!tiersParentId) {
        promises.push(getAllContactByTiers({
          query: {
            archive: false,
            tiersId: [tiersParentId],
            societeId: this.societeActiveId,
          },
        }));
      }
      if (this.withContactPortail && this.modulePortailClientActive && !!tiersId) {
        promises.push(getAllByTiers(tiersId));
      }
      if (!promises.length) {
        if (!contacts || !Array.isArray(contacts)) {
          return [];
        }
        return contacts
          .map(c => ({ ...c, nomPrenom: getContactFullNameWithInfo(c) }))
          .filter(c => !c.archive) || [];
      }
      this.isFetchingContact = true;
      try {
        const results = (await Promise.all(promises))
          .flatMap(r => r.data.result.items);
        return uniqBy((contacts || []).concat(results)
          .map(c => ({ ...c, nomPrenom: getContactFullNameWithInfo(c) }))
          .filter(c => !c.archive), 'id');
      } catch (e) {
        messageErreur(this.$i18n);
        throw e;
      } finally {
        this.isFetchingContact = false;
      }
    },
    actionSelectContact(contact) {
      this.contactSelected = contact;
      this.search = contact?.nomPrenom || '';
    },
    getTypesContacts(typeContactIds) {
      const typesContacts = this.typeContacts.filter(t => typeContactIds.includes(t.typeContactId));
      return typesContacts.length ? typesContacts.map(t => t.libelle).join(', ') : '';
    },
    ajouterContact() {
      this.contactAdded = contactGenerer();
      this.popUpAjoutContact = true;
    },
    actionResetValue() {
      this.contactSelected = null;
      this.search = '';
      this.$emit('reset');
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadParametres([PARAMETRES_KEYS.typeContact]);
      await this.loadParametresInterne([
        PARAMETRES_INTERNE_KEYS.contactsVenteSuperieur.key,
        PARAMETRES_INTERNE_KEYS.modulePortailClient.key,
      ]);
      this.contactsData = await this.getContactData();
      this.search = getContactFullNameWithInfo(this.contactSelected);
    } catch (e) {
      messageErreur(this.$i18n);
      throw e;
    } finally {
      loadingComponent.close();
    }
  },
};
</script>
